import { useEffect, useState } from "react";
import { navigate } from "@reach/router";
import { useSelector } from "react-redux";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { selectCurrentUserId } from "newui/features/Auth/authSelectors";
import { useHasAIEvaluationAccess } from "hooks/featureFlags";
import { useEvaluation } from "../../hooks/useEvaluation";
import { InitialEvaluationProvider } from "../../hooks/InitialEvaluationContext";
import { RfxLeadInfoBox } from "../steps/InitialEvaluationStep/panel/RfxLeadInfoBox";
import type { Rfx, RfxLead } from "newui/features/Rfx/types/rfx.types";
import type { PanelMode } from "../../types/panel.type";
import { InvitationBanner } from "../steps/InitialEvaluationStep/panel";
import { PanelMemberStatus } from "../../types/panel.type";
import { EvaluationModeSelector } from "./EvaluationModeSelector";

interface EvaluationPanelProps {
	rfxData: Rfx;
	rfxSubmissionId?: number;
	isClientView: boolean;
}

export const EvaluationPanel: React.FC<EvaluationPanelProps> = ({
	rfxData,
	rfxSubmissionId,
	isClientView,
}) => {
	const { clientPaths } = useAuthContext();
	const hasAIAccess = useHasAIEvaluationAccess();
	const currentUserId = useSelector(selectCurrentUserId);
	const rfxId = rfxData?.id;
	const rfxLead = rfxData?.rfxLead;

	const {
		hasStarted,
		isLoading,
		evaluationState,
		currentUserContext,
		fetchEvaluationState,
		startEvaluation,
	} = useEvaluation({ rfxId, rfxSubmissionId });

	// Pre-creation: compare current user with rfxLead.id
	// Post-creation: use the backend-derived context
	const isRfxLead = evaluationState
		? (currentUserContext?.isRfxLead ?? false)
		: (currentUserId !== null && rfxLead?.id === currentUserId);

	useEffect(() => {
		fetchEvaluationState();
	}, [fetchEvaluationState]);

	const handleView = (): void => {
		const evaluationId = evaluationState?.id;
		if (evaluationId) {
			navigate(clientPaths.viewEvaluation(rfxId, evaluationId));
		}
	};

	const handleCreate = async (panelMode: PanelMode): Promise<void> => {
		const state = await startEvaluation(panelMode);
		const evaluationId = state?.id;
		if (evaluationId) {
			navigate(clientPaths.viewEvaluation(rfxId, evaluationId));
		}
	};

	const handleAcceptPanelInvite = () => {
		navigate(clientPaths.viewEvaluation(rfxId, evaluationState?.id));
	}

	const handleDeclined = () => {
		fetchEvaluationState();
	}

	if (!isClientView) {
		return null;
	}

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
				<SkeletonBase
					startColor="lightgray"
					width="60%"
					height="40px"
					className="!rounded-lg"
				/>
			</div>
		);
	}

	return (
		<InitialEvaluationProvider rfxId={rfxId}>
		<div data-testid="evaluation-panel">
			<div className="mb-6">
				<h2 className="text-xl font-semibold text-gray-900">
					{hasAIAccess ? (
						<>EvaluA<span className="text-utility-brand-700">I</span>tion tool</>
					) : (
						"Evaluation"
					)}
				</h2>
				<p className="mt-1 text-sm text-gray-500">
					{hasAIAccess
						? "Use AI-assisted evaluation to systematically score and compare supplier submissions."
						: "Systematically score and compare supplier submissions."}
				</p>
			</div>
			<div className="flex items-center gap-4">
				{hasStarted && !isRfxLead && (
					<InvitationBanner evalId={evaluationState?.id} memberId={currentUserContext?.panelMemberId} rfxData={rfxData}
						onAccept={handleAcceptPanelInvite} currentStatus={currentUserContext?.panelMemberStatus} currentStep={evaluationState?.currentStep} onDecline={handleDeclined} />
				)}
				{hasStarted ? (
					<Button
						btntype="primary"
						onClick={handleView}
						data-testid="view-evaluation-btn"
					>
						View Evaluation
					</Button>
				) : isRfxLead ? (
					<EvaluationModeSelector
						onStart={handleCreate}
						hasAIAccess={hasAIAccess}
						isLoading={isLoading}
					/>
				) : (
					<div className="space-y-3" data-testid="evaluation-not-started-info">
						<p className="text-sm text-gray-600">
							RFx Lead has not started the Evaluation Process yet.
						</p>
						{rfxLead && (
							<RfxLeadInfoBox
								fullName={rfxLead.fullName}
								email={rfxLead.email}
								phoneNumber={rfxLead.phoneNumber}
							/>
						)}
					</div>
				)}
			</div>
		</div>
		</InitialEvaluationProvider>
	);
};
